import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGoogleAnalytics } from '../../hooks/useGoogleAnalytics';

const GoogleAnalyticsTracker = () => {
  const location = useLocation();
  const { trackEvent } = useGoogleAnalytics();

  // Track page views automatically
  useEffect(() => {
    // Track page view
    trackEvent('page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname + location.search
    });

    // Track specific page events
    if (location.pathname === '/') {
      trackEvent('homepage_visit');
    } else if (location.pathname === '/contact') {
      trackEvent('contact_page_visit');
    } else if (location.pathname === '/services') {
      trackEvent('services_page_visit');
    } else if (location.pathname === '/about') {
      trackEvent('about_page_visit');
    } else if (location.pathname === '/products') {
      trackEvent('products_page_visit');
    } else if (location.pathname === '/cart') {
      trackEvent('cart_page_visit');
    } else if (location.pathname === '/checkout') {
      trackEvent('checkout_page_visit');
    } else if (location.pathname === '/login') {
      trackEvent('login_page_visit');
    } else if (location.pathname === '/register') {
      trackEvent('register_page_visit');
    } else if (location.pathname === '/dashboard') {
      trackEvent('dashboard_visit');
    } else if (location.pathname.startsWith('/admin')) {
      trackEvent('admin_panel_visit');
    }

  }, [location, trackEvent]);

  // Track user engagement events
  useEffect(() => {
    const trackedScrollPercentages = new Set();
    
    const trackScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      
      // Only track each percentage once per page visit
      if (scrollPercent >= 25 && scrollPercent < 50 && !trackedScrollPercentages.has(25)) {
        trackedScrollPercentages.add(25);
        trackEvent('scroll_25_percent');
      } else if (scrollPercent >= 50 && scrollPercent < 75 && !trackedScrollPercentages.has(50)) {
        trackedScrollPercentages.add(50);
        trackEvent('scroll_50_percent');
      } else if (scrollPercent >= 75 && scrollPercent < 100 && !trackedScrollPercentages.has(75)) {
        trackedScrollPercentages.add(75);
        trackEvent('scroll_75_percent');
      } else if (scrollPercent >= 100 && !trackedScrollPercentages.has(100)) {
        trackedScrollPercentages.add(100);
        trackEvent('scroll_100_percent');
      }
    };

    const trackTimeOnPage = () => {
      setTimeout(() => trackEvent('time_on_page_30_seconds'), 30000);
      setTimeout(() => trackEvent('time_on_page_1_minute'), 60000);
      setTimeout(() => trackEvent('time_on_page_2_minutes'), 120000);
      setTimeout(() => trackEvent('time_on_page_5_minutes'), 300000);
    };

    // Add throttled scroll listener
    let scrollTimeout;
    const throttledTrackScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        trackScroll();
        scrollTimeout = null;
      }, 100); // Throttle to max once per 100ms
    };
    
    window.addEventListener('scroll', throttledTrackScroll, { passive: true });
    
    // Track time on page
    trackTimeOnPage();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', throttledTrackScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [trackEvent]);

  // Track form interactions
  useEffect(() => {
    const trackFormFocus = (e) => {
      try {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
          // Extract only serializable properties to avoid circular reference errors
          const fieldName = e.target.name || e.target.id || 'unknown';
          const formName = e.target.form?.name || 'unknown';
          
          trackEvent('form_field_focus', {
            field_name: fieldName,
            form_name: formName,
            field_type: e.target.type || 'text'
          });
        }
      } catch (error) {
        console.error('Error tracking form focus:', error);
      }
    };

    const trackFormBlur = (e) => {
      try {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
          // Extract only serializable properties to avoid circular reference errors
          const fieldName = e.target.name || e.target.id || 'unknown';
          const formName = e.target.form?.name || 'unknown';
          const valueLength = e.target.value?.length || 0;
          
          trackEvent('form_field_blur', {
            field_name: fieldName,
            form_name: formName,
            field_value_length: valueLength,
            field_type: e.target.type || 'text'
          });
        }
      } catch (error) {
        console.error('Error tracking form blur:', error);
      }
    };

    // Add form interaction listeners
    document.addEventListener('focusin', trackFormFocus);
    document.addEventListener('focusout', trackFormBlur);

    // Cleanup
    return () => {
      document.removeEventListener('focusin', trackFormFocus);
      document.removeEventListener('focusout', trackFormBlur);
    };
  }, [trackEvent]);

  // Track external link clicks
  useEffect(() => {
    const trackExternalLinks = (e) => {
      const link = e.target.closest('a');
      if (link && link.hostname !== window.location.hostname) {
        trackEvent('external_link_click', {
          link_url: link.href,
          link_text: link.textContent?.trim() || 'unknown'
        });
      }
    };

    document.addEventListener('click', trackExternalLinks);

    return () => {
      document.removeEventListener('click', trackExternalLinks);
    };
  }, [trackEvent]);

  // Track file downloads
  useEffect(() => {
    const trackDownloads = (e) => {
      const link = e.target.closest('a');
      if (link && (link.href.includes('.pdf') || link.href.includes('.doc') || link.href.includes('.zip'))) {
        trackEvent('file_download', {
          file_url: link.href,
          file_type: link.href.split('.').pop(),
          file_name: link.textContent?.trim() || 'unknown'
        });
      }
    };

    document.addEventListener('click', trackDownloads);

    return () => {
      document.removeEventListener('click', trackDownloads);
    };
  }, [trackEvent]);

  // Track video interactions
  useEffect(() => {
    const trackVideoEvents = (e) => {
      const video = e.target;
      if (video.tagName === 'VIDEO') {
        if (e.type === 'play') {
          trackEvent('video_play', {
            video_src: video.src,
            video_title: video.title || 'unknown'
          });
        } else if (e.type === 'pause') {
          trackEvent('video_pause', {
            video_src: video.src,
            video_title: video.title || 'unknown',
            current_time: video.currentTime
          });
        } else if (e.type === 'ended') {
          trackEvent('video_complete', {
            video_src: video.src,
            video_title: video.title || 'unknown',
            duration: video.duration
          });
        }
      }
    };

    document.addEventListener('play', trackVideoEvents, true);
    document.addEventListener('pause', trackVideoEvents, true);
    document.addEventListener('ended', trackVideoEvents, true);

    return () => {
      document.removeEventListener('play', trackVideoEvents, true);
      document.removeEventListener('pause', trackVideoEvents, true);
      document.removeEventListener('ended', trackVideoEvents, true);
    };
  }, [trackEvent]);

  // This component doesn't render anything
  return null;
};

export default GoogleAnalyticsTracker;
